import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaBrain,
  FaQuestionCircle,
  FaTrophy,
  FaGift,
  FaGlobeAmericas,
  FaCalendarDay,
  FaCameraRetro,
} from 'react-icons/fa';
import { BsChatRightText } from 'react-icons/bs';
import logo from '../assets/ZoomedInTransparentComp.png';
import HowToPlay from './HowToPlay';
import PointsBreakdownModal from './points/PointsBreakdownModal';
import SeasonRecapBanner from './points/SeasonRecapBanner';
import ReferralInviteModal from './referral/ReferralInviteModal';
import InviteFriendsButton from './referral/InviteFriendsButton';
import ReferralTopBarPromo from './referral/ReferralTopBarPromo';
import HomeTopBar from './home/HomeTopBar';
import PuzzleBackground from './home/PuzzleBackground';
import { useProfile } from '../contexts/ProfileContext';
import { GUEST_DEMO_BUTTON_LABEL } from '../constants/games/demoModeCopy';
import './HomePage.css';

const VERIFICATION_TOAST_STORAGE_KEY = 'zi-auth:show-verify-toast:v1';
const WELCOME_TOAST_STORAGE_KEY = 'zi-auth:show-welcome-toast:v1';
const VERIFICATION_TOAST_DURATION_MS = 3200;
const REFERRAL_REFRESH_HINT_STORAGE_KEY = 'zi-auth:show-referral-refresh-hint:v1';
const REFERRAL_PROMO_DISMISS_STORAGE_KEY = 'zi:referral-banner-dismissed';
const FREE_NO_SIGN_UP_REQUIRED_LABEL = 'FREE AND NO SIGN-UP REQUIRED';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, loading, refreshCoins } = useProfile();
  const [showHelp, setShowHelp] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralPromoDismissed, setReferralPromoDismissed] = useState(() => {
    try {
      return localStorage.getItem(REFERRAL_PROMO_DISMISS_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [homeToastMessage, setHomeToastMessage] = useState('');
  const [isLogoBursting, setIsLogoBursting] = useState(false);
  const logoBurstFrameRef = useRef(null);
  const showClassicModes = false;
  const showUnlimitedGuestCta = !loading && !user;

  const dismissReferralPromo = () => {
    setReferralPromoDismissed(true);
    try {
      localStorage.setItem(REFERRAL_PROMO_DISMISS_STORAGE_KEY, '1');
    } catch {
      // ignore
    }
  };

  const triggerLogoBurst = () => {
    if (logoBurstFrameRef.current !== null) {
      window.cancelAnimationFrame(logoBurstFrameRef.current);
    }

    setIsLogoBursting(false);
    logoBurstFrameRef.current = window.requestAnimationFrame(() => {
      setIsLogoBursting(true);
      logoBurstFrameRef.current = null;
    });
  };

  const handleLogoPointerEnter = (event) => {
    if (event.pointerType === 'mouse') {
      triggerLogoBurst();
    }
  };

  const handleLogoPointerDown = () => {
    triggerLogoBurst();
  };

  useEffect(() => {
    try {
      const shouldShowVerificationToast =
        typeof window !== 'undefined' &&
        window.sessionStorage.getItem(VERIFICATION_TOAST_STORAGE_KEY) === '1';
      const shouldShowWelcomeToast =
        typeof window !== 'undefined' &&
        window.sessionStorage.getItem(WELCOME_TOAST_STORAGE_KEY) === '1';
      const shouldShowReferralRefreshHint =
        typeof window !== 'undefined' &&
        window.sessionStorage.getItem(REFERRAL_REFRESH_HINT_STORAGE_KEY) === '1';

      if (!shouldShowVerificationToast && !shouldShowWelcomeToast && !shouldShowReferralRefreshHint) {
        return undefined;
      }

      window.sessionStorage.removeItem(VERIFICATION_TOAST_STORAGE_KEY);
      window.sessionStorage.removeItem(WELCOME_TOAST_STORAGE_KEY);
      window.sessionStorage.removeItem(REFERRAL_REFRESH_HINT_STORAGE_KEY);

      const baseToastMessage =
        shouldShowWelcomeToast && shouldShowVerificationToast
          ? 'Welcome to Zoomed In. Email verified successfully.'
          : shouldShowWelcomeToast
            ? 'Welcome to Zoomed In. Your account is ready.'
            : shouldShowVerificationToast
              ? 'Email verified successfully.'
              : 'Welcome to Zoomed In.';
      const referralHintMessage = shouldShowReferralRefreshHint
        ? 'Referral bonus detected. If your coins still show 250, refresh once (expected: 400).'
        : '';
      const nextToastMessage = `${baseToastMessage}${referralHintMessage ? ` ${referralHintMessage}` : ''}`;
      setHomeToastMessage(nextToastMessage);

      if (shouldShowWelcomeToast || shouldShowReferralRefreshHint) {
        // Keep coins UI in sync after auth callback payouts (e.g. referral signup).
        refreshCoins?.().catch(() => {});
      }

      const toastDurationMs = shouldShowReferralRefreshHint ? 7000 : VERIFICATION_TOAST_DURATION_MS;
      const timeoutId = window.setTimeout(() => {
        setHomeToastMessage('');
      }, toastDurationMs);

      return () => window.clearTimeout(timeoutId);
    } catch {
      return undefined;
    }
  }, [refreshCoins]);

  useEffect(() => {
    if (!isLogoBursting) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsLogoBursting(false);
    }, 760);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLogoBursting]);

  useEffect(() => {
    return () => {
      if (logoBurstFrameRef.current !== null) {
        window.cancelAnimationFrame(logoBurstFrameRef.current);
      }
    };
  }, []);

  return (
    <div className="homepage">
      <PuzzleBackground />
      <HomeTopBar
        onPointsClick={() => setShowPointsModal(true)}
        beforePills={
          user && referralPromoDismissed ? (
            <InviteFriendsButton onClick={() => setShowReferralModal(true)} />
          ) : null
        }
        afterCoins={
          user && !referralPromoDismissed ? (
            <ReferralTopBarPromo
              onInviteClick={() => setShowReferralModal(true)}
              onDismiss={dismissReferralPromo}
            />
          ) : null
        }
      />
      {homeToastMessage && (
        <div className="homepage-toast" role="status" aria-live="polite">
          {homeToastMessage}
        </div>
      )}

      {showPointsModal && (
        <PointsBreakdownModal onClose={() => setShowPointsModal(false)} />
      )}
      {showReferralModal && (
        <ReferralInviteModal onClose={() => setShowReferralModal(false)} />
      )}
      {showHelp && <HowToPlay onClose={() => setShowHelp(false)} />}

      <div className="homepage-content">
        <SeasonRecapBanner />
        <img
          src={logo}
          alt="Zoomed In"
          className={`homepage-logo ${isLogoBursting ? 'is-bursting' : ''}`}
          onPointerEnter={handleLogoPointerEnter}
          onPointerDown={handleLogoPointerDown}
        />
        <p className="homepage-description">
          Guess the word using zoomed-in images, riddles, and clues. Sometimes the clues help...
          <strong>sometimes they mislead</strong>.
        </p>

        <div className="game-modes">
          {showClassicModes && (
            <>
              <button className="play-button" onClick={() => navigate('/play')}>
                <span className="play-button-icon"><FaSearch /></span>
                <span className="play-button-text">
                  <span className="play-button-title">Zoomed In</span>
                </span>
              </button>
              <button className="play-button hardcore" onClick={() => navigate('/hardcore')}>
                <span className="play-button-icon"><FaSearch /></span>
                <span className="play-button-text">
                  <span className="play-button-title">Zoomed In Hardcore</span>
                  <span className="play-button-subtitle">60s Timer</span>
                </span>
              </button>
            </>
          )}

          <button className="play-button daily" onClick={() => navigate('/daily')}>
            <span className="play-button-icon"><FaCalendarDay /></span>
            <span className="play-button-text">
              <span className="play-button-title">Zoomed In Daily</span>
              <span className="play-button-subtitle">One shared puzzle to rule them all</span>
              {showUnlimitedGuestCta && (
                <span className="play-button-guest-note">
                  {FREE_NO_SIGN_UP_REQUIRED_LABEL}
                </span>
              )}
            </span>
          </button>

          <button className="play-button custom" onClick={() => navigate('/custom/create')}>
            <span className="play-button-icon"><FaCameraRetro /></span>
            <span className="play-button-text">
              <span className="play-button-title">Zoomed In Custom</span>
              <span className="play-button-subtitle">
                {user
                  ? 'Create your own game or manage your existing ones'
                  : 'Play any shared custom link (creator must be signed in)'}
              </span>
            </span>
          </button>

          <button className="play-button unlimited" onClick={() => navigate('/unlimited')}>
            <span className="play-button-icon"><FaSearch /></span>
            <span className="play-button-text">
              <span className="play-button-title">Zoomed In Unlimited</span>
              <span className="play-button-subtitle">Powered by Pexels</span>
              {showUnlimitedGuestCta && (
                <span className="play-button-guest-note">
                  {FREE_NO_SIGN_UP_REQUIRED_LABEL}
                </span>
              )}
            </span>
          </button>

          <button className="play-button generations" onClick={() => navigate('/generations')}>
            <span className="play-button-icon"><FaBrain /></span>
            <span className="play-button-text">
              <span className="play-button-title">Zoomed In Generations</span>
              <span className="play-button-subtitle">
                {user ? 'AI Generated • 100 coins / round' : 'AI Generated'}
              </span>
            </span>
          </button>

          <button className="play-button generations" onClick={() => navigate('/earth')}>
            <span className="play-button-icon"><FaGlobeAmericas /></span>
            <span className="play-button-text">
              <span className="play-button-title">Zoomed In Earth</span>
              <span className="play-button-subtitle">
                {user ? 'Street View Guessing • 25 coins / game' : 'Street View Guessing'}
              </span>
              {showUnlimitedGuestCta && (
                <span className="play-button-guest-note">
                  {GUEST_DEMO_BUTTON_LABEL}
                </span>
              )}
            </span>
          </button>

          <button className="play-button riddled-in-home" onClick={() => navigate('/riddled-in')}>
            <span className="play-button-icon"><BsChatRightText /></span>
            <span className="play-button-text">
              <span className="play-button-title">Riddled-In</span>
              <span className="play-button-subtitle">
                {user ? 'Solve the riddle • 20 coins / game' : 'Solve the riddle'}
              </span>
              {showUnlimitedGuestCta && (
                <span className="play-button-guest-note">
                  {GUEST_DEMO_BUTTON_LABEL}
                </span>
              )}
            </span>
          </button>

          {user && (
            <button
              className="play-button invite-friends-home-mobile"
              onClick={() => setShowReferralModal(true)}
            >
              <span className="play-button-icon"><FaGift /></span>
              <span className="play-button-text">
                <span className="play-button-title">Invite Friends</span>
                <span className="play-button-subtitle">Earn 500 &bull; Friends get bonus 150</span>
              </span>
            </button>
          )}

          <button
            className={`play-button leaderboard-home${user ? ' leaderboard-home--signed-in-mobile' : ''}`}
            onClick={() => navigate('/leaderboard')}
          >
            <span className="play-button-icon"><FaTrophy /></span>
            <span className="play-button-text">
              <span className="play-button-title">Leaderboards</span>
            </span>
          </button>

          <button
            className={`play-button how-to-play${user ? ' how-to-play--signed-in-mobile' : ''}`}
            onClick={() => setShowHelp(true)}
          >
            <span className="play-button-icon"><FaQuestionCircle /></span>
            <span className="play-button-text">
              <span className="play-button-title">How To Play</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
